import { Component } from '@theme/component';
// CUSTOM: ReAmour_StickyAddToCart_imports START
// Horizon 標準では variantUpdate / morph / fly-to-cart 等を使うが、
// ReAmour 仕様（モーダルを開くだけのトリガーボタン）では不要のため未使用 import を削除。
// CUSTOM: ReAmour_StickyAddToCart_imports END

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 */

/**
 * CUSTOM: ReAmour_StickyAddToCart_class
 *
 * ReAmour 仕様の追従バー custom element。
 *
 * 仕様：
 * - c_buy-buttons.liquid のカート追加領域（PC: バリエーション一覧 /
 *   モバイル: #open-variant-modal-btn）が画面外になったら追従バーを表示
 * - 追従バー内ボタンクリックで既存モーダル #variant-modal を開く
 *   （#open-variant-modal-btn を疑似クリックして既存 IIFE 内の openModal を起動）
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #anchorObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #currentAnchor = null;

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupIntersectionObserver();
    this.#setupTriggerClick();
    this.#setupResponsiveSwitch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#anchorObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#abortController.abort();
    if (this.#mediaQuery) {
      this.#mediaQuery.removeEventListener('change', this.#handleViewportChange);
    }
  }

  /**
   * 現在のビューポート（PC / モバイル）に対応する観察対象アンカーを返す。
   * - PC（>=750px）: .variant-list__buy-buttons.u__pc[data-reamour-sticky-anchor="pc"]
   * - モバイル（<750px）: #open-variant-modal-btn[data-reamour-sticky-anchor="mobile"]
   *
   * @returns {HTMLElement | null}
   */
  #resolveActiveAnchor() {
    const isPc = window.matchMedia('(min-width: 750px)').matches;
    const selector = isPc
      ? '[data-reamour-sticky-anchor="pc"]'
      : '[data-reamour-sticky-anchor="mobile"]';
    return document.querySelector(selector);
  }

  /**
   * 観察対象のアンカー要素を IntersectionObserver で監視。
   * 画面外（上方向にスクロール抜け）になったら追従バーを表示する。
   */
  #setupIntersectionObserver() {
    const anchor = this.#resolveActiveAnchor();
    if (!anchor) {
      // フォールバック：見つからない場合は何もしない（既存挙動を壊さない）
      console.warn('[ReAmour StickyAddToCart] 観察対象アンカーが見つかりません');
      return;
    }
    this.#currentAnchor = anchor;

    const footer =
      document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    // アンカー可視性 observer
    this.#anchorObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        // アンカーが画面上方向に抜けた時のみ表示（下方向の未到達では表示しない）
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });
    this.#anchorObserver.observe(anchor);

    // フッター可視性 observer（フッターが見えたら追従バー非表示）
    if (footer) {
      this.#footerObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            const currentAnchor = this.#currentAnchor;
            if (!currentAnchor) return;
            const rect = currentAnchor.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top < 0) {
              this.#hiddenByBottom = false;
              this.#showStickyBar();
            }
          }
        },
        { rootMargin: '200px 0px 0px 0px' }
      );
      this.#footerObserver.observe(footer);
    }
  }

  /**
   * CUSTOM: ReAmour_StickyAddToCart_trigger_dispatch
   *
   * 追従バー内トリガーボタンのクリックハンドラ。
   *
   * 旧実装は #open-variant-modal-btn を `.click()` 疑似クリックして
   * c_buy-buttons.liquid の IIFE 内 openModal を起動していたが、
   * 「2段の伝言ゲーム」になり以下の理由で不安定だった：
   *   - PC では #open-variant-modal-btn が display:none で疑似クリックが
   *     Horizon の capture 委譲に拾われ得る
   *   - 同期/部分配信で IIFE が未登録だと無反応になる
   *
   * → カスタムイベント `reamour:open-variant-modal` を document に dispatch し、
   *    c_buy-buttons.liquid の IIFE 側 listener に直接モーダルを開かせる。
   *    さらに IIFE 未応答時のフォールバックとして #variant-modal に
   *    is-open を直接付与し、PC/モバイル両方で確実にモーダルを開く。
   */
  #setupTriggerClick() {
    const { signal } = this.#abortController;
    const trigger = this.querySelector('[data-reamour-sticky-trigger]');
    if (!trigger) return;

    trigger.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#openVariantModal();
      },
      { signal }
    );
  }

  /**
   * 既存モーダル #variant-modal を開く。
   * c_buy-buttons.liquid の IIFE listener へカスタムイベントを通知し、
   * IIFE 側の openModal() で開閉・スクロールロックを統一管理させる。
   *
   * 旧実装は requestAnimationFrame での is-open 直接付与フォールバックを
   * 持っていたが、IIFE 未初期化時にフォールバックが走ると以下の問題があった：
   *   - スクロールロックが効かない
   *   - ×／閉じる／背景／ESC のリスナーが無いので閉じられない
   *   - 結果：操作不能モーダルになる
   * IIFE は DOMContentLoaded で初期化され、ユーザーがクリック可能になる時点で
   * 必ず登録済み（クリック→dispatch の経路では未初期化が起こり得ない）のため
   * フォールバックは削除し、IIFE 経路に一本化する。
   */
  #openVariantModal() {
    const modal = document.getElementById('variant-modal');
    if (!modal) {
      console.warn('[ReAmour StickyAddToCart] #variant-modal が見つかりません');
      return;
    }
    document.dispatchEvent(new CustomEvent('reamour:open-variant-modal'));
  }

  /**
   * 750px 境界をまたいでリサイズした場合に、観察対象アンカーを再構築する。
   * 例：PC幅で開いた後にデベロッパーツールでモバイル幅へ切替えた時等。
   */
  #setupResponsiveSwitch() {
    this.#mediaQuery = window.matchMedia('(min-width: 750px)');
    this.#mediaQuery.addEventListener('change', this.#handleViewportChange);
  }

  /** @type {(event: MediaQueryListEvent) => void} */
  #handleViewportChange = () => {
    this.#anchorObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#hideStickyBar();
    this.#hiddenByBottom = false;
    this.#setupIntersectionObserver();
  };

  /** 追従バーを表示状態に切り替え */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /** 追従バーを非表示状態に切り替え */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
